/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import ReactJWPlayer from "react-jw-player"; // *** JWPLAYER
import YouTube from "react-youtube";
import Draggable from "react-draggable";

// import { Dialog, DialogContent, DialogTitle } from "@material-ui/core"; // *** YOUTUBE

// *** JWPLAYER *** BEGIN
import { Dialog, DialogContent, DialogTitle, Button } from "@material-ui/core";
import { PlayCircleFilled, Replay } from "@material-ui/icons";
import keys from "client/config/keys.ts";
import { progressCircles } from "components/Form/FormField";
// *** JWPLAYER *** END

import Paper, { PaperProps } from "@material-ui/core/Paper";

import { selectorOwns } from "client/selectors/ownSelector";

import { AllOwns } from "client/reducers/ownReducer";
import { ScreenInfo } from "client/reducers/modalReducer";

import { ClsrmActionType, LectureInfo } from "./Classroom";
import { VideoPlayer } from "./VideoPlayer";
import { idRestart, idResume, idTestInfo } from "assets/jss/views/ElementIDs";
import { setPlayerState, ApricotAction } from "client/actions/modal";

import playerZoneStyle from "assets/jss/views/classroomPage/playerZoneStyle";
const styles = createStyles(playerZoneStyle);

export type LoadPlayerInfo = {
  load: boolean; // For JWPlayer only - true: open player; false: PlayerBox (not used in YouTube)
  vPos: number;
  pip: boolean;
};

export enum PlayerState {
  Unstarted = -1,
  Ended, // 0
  Playing, // 1
  Paused, // 2
}

function PaperComponent(props: PaperProps) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper
        style={{ position: "absolute", right: 0, top: "3.5rem" }}
        {...props}
      />
    </Draggable>
  );
}

interface Props extends WithStyles<typeof styles> {
  setPlayerState: ApricotAction;
  // store
  playerState: PlayerState;
  owns: AllOwns;
  screenInfo: ScreenInfo;
  classes: any;
  // component props:
  width: string;
  cid: string;
  lid: string;
  lecInfo: LectureInfo;
  loadPlayer: LoadPlayerInfo;
  onAction: (params: ClsrmActionType) => void;
  pip: boolean;
  player: VideoPlayer;
}

const PlayerZoneBase: React.FC<Props> = ({
  setPlayerState,
  playerState,
  owns,
  screenInfo,
  classes,
  width,
  cid,
  lid,
  lecInfo,
  loadPlayer,
  onAction,
  pip,
  player,
}: Props) => {
  const [loadInfo, setLoadInfo] = useState<LoadPlayerInfo>({
    load: false,
    vPos: 0,
    pip: false,
  });
  const [posUpdate, setPosUpdate] = useState<ClsrmActionType | null>(null);

  const [testInfo, setTestInfo] = useState<string | null>(null);

  let videoLen: number | undefined = undefined;
  const refPos = useRef(-60); // previous position tracked
  const refPrg = useRef(0); // previous progress tracked

  useEffect(() => {
    refPos.current = -60;
    refPrg.current = 0;
    setPlayerState(PlayerState.Unstarted); // trigger clearInterval
    setPosUpdate(null); // clear the timer for write to DB
  }, [lid]);

  useEffect(() => {
    setLoadInfo(loadPlayer);
  }, [loadPlayer]);

  useEffect(() => {
    // Check position every 20sec after each playing start
    let timer: any;
    if (playerState === PlayerState.Playing) {
      onAction({ action: "setCourse" });
      timer = setInterval(() => updateProgress(), 20000);
    }
    return () => {
      clearInterval(timer);
    };
  }, [playerState]);

  useEffect(() => {
    // when player size change, write the pos to db right away
    if (width !== "0px") {
      updateProgress(true);
    }
  }, [width]);

  useEffect(() => {
    // A four seconds timer to prevent frequent write to DB
    let timer: any;
    if (posUpdate) {
      timer = setTimeout(() => {
        if (posUpdate) {
          onAction(posUpdate);
        }
      }, 4000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [posUpdate]);

  // Update 'vPos' in Own record, with 'progress' optionally.
  // 'progress' sets the milestone per 10%.
  const updateProgress = (noWait: boolean = false) => {
    if (!player) return;

    let vPos = Math.floor(player.getPosition());
    if (vPos === refPos.current) {
      return;
    }

    if (!videoLen) {
      videoLen = Math.floor(player.getDuration());
    }

    let progress = Math.floor((vPos / videoLen) * 10);
    if (progress === 10) {
      vPos = 0; // when completed, reset vPos
    }
    refPos.current = vPos || -60; // if vPos is 0, reset prevPosition

    var params: ClsrmActionType = {
      action: "setProgress",
      obj: { videoPos: vPos },
    };

    if (!lecInfo.completed && progress !== refPrg.current) {
      // If the lecture isn't completed yet, update progress when it's changed
      refPrg.current = progress;
      params.num = progress;
    }
    if (noWait) {
      setLoadInfo({ ...loadInfo, vPos });
      onAction(params);
    } else {
      setPosUpdate(params); // trigger useEffect to start the timer for writing to DB
    }

    // console.log(">> vPos:", vPos, "  refPos.current:", refPos.current, "  len:", videoLen);
    // console.log(">> progress:", progress, "  refPrg.current:", refPrg.current);
  };

  const sizeConvertor = (width: string) => {
    const suffix = width.substr(width.length - 2);
    const value = parseInt(width.slice(0, width.length - 2), 10);

    if (suffix === "px") {
      return value;
    } else if (suffix === "vw") {
      const w =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.getElementsByTagName("body")[0].clientWidth;
      return (w * value) / 100;
    } else return 0;
  };

  const renderYouTube = (width: string, owned: boolean) => {
    const w = sizeConvertor(width);
    const h = (w * 9) / 16;

    const opts = {
      height: h.toString(10),
      width: w.toString(10),
      playerVars: {
        // Enable API. Don't set autoplay, since embedded+autoplay doesn't add view count
        enablejsapi: 1,

        // instead of seekTo() in onReady, which causes autoplay, use 'start' for init pos
        start: Math.floor(loadInfo.vPos), // floating is wrongfully treated as '0'

        // Set playsinline so it won't always play fullscreen on iPhone
        playsinline: 1,

        // fullscreen doesn' work in our pip
        fs: pip ? 0 : 1,

        // only show related video from our channel
        rel: 0,
      },
    };

    const eventHandler = (state: PlayerState) =>
      owned
        ? (e: any) => {
            if (window.e2e === "on" && state === PlayerState.Playing) {
              setTestInfo(`vPos:${Math.floor(player.getPosition())}`);
            }
            setPlayerState(state);
            // when video ends, updateProgress w/o wait
            updateProgress(state === PlayerState.Ended);
          }
        : undefined;

    return (
      <>
        <YouTube
          id={idRestart}
          key={`${lecInfo.ybody}-${pip ? 1 : 0}-${loadInfo.vPos}`}
          videoId={lecInfo.ybody}
          opts={opts}
          onReady={(e) => {
            onAction({ action: "setPlayer", obj: e.target, str: "yt" });
          }}
          onPause={eventHandler(PlayerState.Paused)}
          onPlay={eventHandler(PlayerState.Playing)}
          onEnd={eventHandler(PlayerState.Ended)}
        />
        {testInfo && (
          <div
            id={idTestInfo}
            style={{ position: "fixed", color: "transparent" }}>
            {testInfo}
          </div>
        )}
      </>
    );
  };

  // *** JWPLAYER *** BEGIN
  //
  const renderPlayerBoxButtons = () => {
    const { vPos } = loadPlayer;
    return (
      !lecInfo.sched && (
        <div className={classes.playerBttons}>
          {vPos !== 0 &&
            playerBoxButton(vPos, "Resume", <PlayCircleFilled />, idResume)}
          {playerBoxButton(
            0,
            vPos !== 0 ? "Restart" : "Start",
            vPos !== 0 ? <Replay /> : <PlayCircleFilled />,
            idRestart,
          )}
        </div>
      )
    );
  };

  const playerBoxButton = (
    vPos: number,
    label: string,
    icon: React.ReactNode,
    id: string,
  ) => {
    const widerScreen = screenInfo.width >= 768;
    return (
      <Button
        id={id}
        className={classes.playerBtn}
        startIcon={icon}
        size={widerScreen ? "large" : "medium"}
        color="secondary"
        variant="contained"
        onClick={() => setLoadInfo({ load: true, vPos, pip })}
        style={{
          fontSize: widerScreen ? "1.5rem" : "1.2rem",
          width: widerScreen ? "14rem" : "auto",
        }}>
        {label}
      </Button>
    );
  };

  const renderPlayerBox = (width: string) => {
    onAction({ action: "setPlayer", obj: null }); // *** JWPLAYER

    // width is a string ends with "px"
    if (width === "0px" || typeof width !== "string") return "";

    const playerWidth = parseInt(width.slice(0, -2), 10);
    const body = (
      <div
        className={classes.playerBoxContent}
        style={{ fontSize: `${playerWidth / 15}px` }}>
        {!pip && <span style={{ lineHeight: "120%" }}>{lecInfo.title}</span>}
        {renderPlayerBoxButtons()}
      </div>
    );

    return (
      <div
        className={classes.playerBox}
        style={{ width, margin: `0 calc((100% - ${width}) / 2)` }}>
        {body}
      </div>
    );
  };

  const renderJWPlayer = (
    owned: boolean, // remove owned parameter when we settle the business strategy
  ) => {
    const playerId = "phoenix-v";
    const eventHandler = (state: PlayerState) =>
      owned
        ? (e: any) => {
            setPlayerState(state);
            updateProgress();
          }
        : undefined;

    return (
      <>
        {window.jwplayer ? <></> : progressCircles()}
        <ReactJWPlayer
          playerId={playerId}
          isMuted={false}
          playerScript={keys.playerScript}
          file={lecInfo.body}
          onReady={(e: any) => {
            const jwplayer = window.jwplayer(playerId);
            onAction({ action: "setPlayer", obj: jwplayer, str: "jw" });
            jwplayer.seek(loadInfo.vPos);

            videoLen = Math.floor(jwplayer.getDuration());
            if (loadInfo.vPos === 0 && owned) {
              updateProgress();
            }
          }}
          onPause={eventHandler(PlayerState.Paused)}
          onPlay={eventHandler(PlayerState.Playing)}
          onResume={eventHandler(PlayerState.Playing)}
          onOneHundredPercent={eventHandler(PlayerState.Ended)}
        />
      </>
    );
  };
  //
  // *** JWPLAYER *** END

  const renderPlayerInstance = (width: string) => {
    const owned = owns && owns[cid] ? true : false;
    return (
      <div
        className={classes.player}
        style={{
          width,
          overflow: "hidden", // hide unnecessary scrollbar
          margin: `0 calc((100% - ${width}) / 2)`,
        }}>
        {lecInfo.ybody ? renderYouTube(width, owned) : renderJWPlayer(owned)}
      </div>
    );
  };

  const renderAction = (width: string) => {
    // width is a string ends with "px"
    if (width === "0px" || typeof width !== "string") return "";

    const widerScreen = screenInfo.width >= 768;
    const playerWidth = parseInt(width.slice(0, -2), 10);
    const body = (
      <div
        className={classes.playerBoxContent}
        style={{ fontSize: `${playerWidth / 15}px` }}>
        <span style={{ lineHeight: "120%", margin: "1rem 0" }}>
          {lecInfo.title}
        </span>
        <span
          style={{
            lineHeight: "120%",
            fontFamily: "Open Sans",
            fontSize: `${playerWidth / 32}px`,
          }}>
          {lecInfo.body}
        </span>
        <Button
          // id={id}
          className={classes.playerBtn}
          color="secondary"
          variant="contained"
          onClick={() => onAction({ action: "ibx", str: lecInfo.length })}
          style={{
            fontSize: widerScreen ? "1.5rem" : "1.2rem",
            padding: "0.75rem 2rem",
          }}>
          {lecInfo.ybody}
        </Button>
      </div>
    );

    return (
      <div
        className={classes.playerBox}
        style={{ width, margin: `0 calc((100% - ${width}) / 2)` }}>
        {body}
      </div>
    );
  };

  const playerContent = (playerWidth: string) => {
    if (lecInfo.act) {
      return renderAction(playerWidth);
    }
    // *** If we have YOUTUBE Only ***
    // return renderPlayerInstance(playerWidth);

    // *** JWPLAYER / YOUTUBE ***
    //
    if (lecInfo.ybody || loadInfo.load) {
      return renderPlayerInstance(playerWidth);
    } else {
      return renderPlayerBox(playerWidth);
    }
  };

  // make sure the loadInfo is for correct player instance
  if (pip !== loadInfo.pip) {
    return <></>;
  }

  const pipWidth = () =>
    screenInfo.orientation === "portrait" ? "60vw" : "30vw";

  return pip ? (
    <Dialog
      classes={{ root: classes.pipRoot, paper: classes.pipPaper }}
      hideBackdrop
      open={true}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title">
      <DialogTitle
        classes={{ root: classes.pipTitleBar }}
        style={{ maxWidth: pipWidth() }}
        id="draggable-dialog-title">
        <div className={classes.pipTitleText}>{lecInfo.title}</div>
      </DialogTitle>
      <DialogContent
        classes={{
          root:
            screenInfo.orientation === "portrait"
              ? classes.pipPlayerL
              : classes.pipPlayerS,
        }}>
        {playerContent(pipWidth())}
      </DialogContent>
    </Dialog>
  ) : (
    <div style={{ background: "inherit" }}>{playerContent(width)}</div>
  );
};

function mapStateToProps(state: any) {
  return {
    owns: selectorOwns(state),
    screenInfo: state.state_modal.screenInfo,
    playerState: state.state_modal.playerState,
  };
}

const base: any = PlayerZoneBase;
export const PlayerZone: any = React.memo(
  withStyles(styles)(connect(mapStateToProps, { setPlayerState })(base)),
);

export const PlayerZoneTest = withStyles(styles)(PlayerZoneBase);

export const playerActivated = (state: PlayerState) =>
  state === PlayerState.Playing || state === PlayerState.Paused;
